import React, { useEffect } from "react";
import {
    Divider,
    Grid,
    ListItem,
    ListItemText,
    Typography
} from "@mui/material/";
import { useAssetTypeIdentifiers } from "../../../../common/hooks";
import { strings } from "../../../../common/i18n";
import actions from "../../../../Store/actions";
import { logToSentry } from "../../../../config/Sentry";

const makeText = (search: string = "", text: string = "", label: string) => {
    let body = text || "";
    if (search && text) {
        const startIndex = text.toLowerCase().indexOf(search.toLowerCase());
        if (startIndex >= 0) {
            const endIndex = startIndex + search.length;
            body = (
                <>
                    {text.substring(0, startIndex)}
                    {
                        <Typography color="primary" component="b">
                            <u>{text.substring(startIndex, endIndex)}</u>
                        </Typography>
                    }
                    {text.substring(endIndex)}
                </>
            );
        }
    }

    return (
        <>
            <Grid item xs={6}>
                <Typography variant="caption">{`${label} :`}</Typography>
                <Typography variant="inherit" title={body || "\u2014"}>
                    {body || "\u2014"}
                </Typography>
            </Grid>
        </>
    );
};

type Props = {
    altAsset: any,
    readonly: boolean,
    onValueChange: Function,
    value: any
};

/**
 * Alt-asset Matches Component
 */
export default ({ altAsset, readonly, onValueChange, value }: Props) => {
    const identifiers = useAssetTypeIdentifiers(altAsset ? altAsset.type : 0);

    useEffect(() => {
        if (altAsset.assetAuxiliary !== null) {
            const next = altAsset.assetMatches.find(
                (item) =>
                    item.auxiliary &&
                    item.auxiliary.id === altAsset.assetAuxiliary.id
            );
            next && onValueChange && onValueChange(next, false);
        }
    }, [altAsset]);

    const data = React.useMemo(() => {
        if (altAsset && altAsset.assetMatches.length > 0) {
            return altAsset.assetMatches.map((item) => item.asset);
        }
        return [];
    }, [altAsset]);

    const handleClick = (index: number) => () => {
        try {
            const assetMatch = altAsset.assetMatches[index];
            if (assetMatch) {
                onValueChange && onValueChange(assetMatch, true);
            } else {
                onValueChange && onValueChange(null, false);
            }
        } catch (error) {
            const errStr = error.toString();
            console.error(errStr);
            logToSentry("On selecting match:", { error });
            dispatch(
                actions.toast.create.error(strings("errors.onSelectingMatch"))
            );
        }
    };

    const isDisabled = (item: any) =>
        altAsset.isResolved || (value && item.id !== value.asset.id);

    const isSelected = (item: any) => value && item.id === value.asset.id;

    return (
        <Grid item xs={12}>
            {data.map((item, index) => (
                <React.Fragment key={item.id}>
                    <ListItem
                        button
                        disabled={isDisabled(item)}
                        onClick={handleClick(index)}
                        selected={isSelected(item)}
                    >
                        <Grid container>
                            {makeText(
                                altAsset.identifier1,
                                item.identifier1,
                                identifiers.identifier1
                            )}
                            {identifiers.identifier2 !== undefined
                                ? makeText(
                                      altAsset.identifier2,
                                      item.identifier2,
                                      identifiers.identifier2
                                  )
                                : null}
                            {identifiers.identifier3 !== undefined
                                ? makeText(
                                      altAsset.identifier3,
                                      item.identifier3,
                                      identifiers.identifier3
                                  )
                                : null}
                            {identifiers.identifier4 !== undefined
                                ? makeText(
                                      altAsset.identifier4,
                                      item.identifier4,
                                      identifiers.identifier4
                                  )
                                : null}
                        </Grid>
                    </ListItem>
                    <Divider />
                </React.Fragment>
            ))}
            {data.length === 0 && (
                <ListItem>
                    <ListItemText
                        primary={strings("messages.noAssetMatches")}
                    />
                </ListItem>
            )}
        </Grid>
    );
};
